import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const KeyboardContext = createContext(null);

export const useKeyboard = () => useContext(KeyboardContext);

// Supported types: 'numeric' | 'alpha' | 'email'
export const KeyboardProvider = ({ children }) => {
  const [visible, setVisible] = useState(false);
  const [type, setType] = useState('numeric');
  const [options, setOptions] = useState({}); // e.g., { allowDecimal, maxLength }
  const [handlers, setHandlers] = useState({
    onInput: null,    // (text) => void
    onBackspace: null,// () => void
    onEnter: null,    // () => void
    onClose: null     // () => void
  });

  // Prefetch keyboard modules in the background so the first open feels instant
  React.useEffect(() => {
    // webpackPrefetch hints help when supported
    import(/* webpackPrefetch: true */ '../components/common/keyboard/NumericKeypad');
    import(/* webpackPrefetch: true */ '../components/common/keyboard/AlphaKeyboard');
  }, []);

  const open = useCallback((kbType = 'numeric', opts = {}, h = {}) => {
    setType(kbType);
    setOptions(opts || {});
    setHandlers({
      onInput: h.onInput || null,
      onBackspace: h.onBackspace || null,
      onEnter: h.onEnter || null,
      onClose: h.onClose || null
    });
    setVisible(true);
  }, []);

  const close = useCallback(() => {
    setVisible(false);
    handlers.onClose && handlers.onClose();
  }, [handlers]);

  const api = useMemo(() => ({
    visible,
    type,
    options,
    open,
    close,
    // Pass-through handlers
    onInput: (ch) => handlers.onInput && handlers.onInput(ch),
    onBackspace: () => handlers.onBackspace && handlers.onBackspace(),
    onEnter: () => handlers.onEnter && handlers.onEnter(),
  }), [visible, type, options, open, close, handlers]);

  return (
    <KeyboardContext.Provider value={api}>
      {children}
    </KeyboardContext.Provider>
  );
};

// Global overlay that decides which keyboard to show
export const KeyboardOverlay = () => {
  const kb = useKeyboard();
  if (!kb?.visible) return null;

  const backdropStyle = {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    top: 0,
    background: 'rgba(0,0,0,0.35)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent',
    userSelect: 'none'
  };

  const panelStyle = {
    width: '100%',
    maxWidth: 1200,
    background: '#fff',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    boxShadow: '0 -4px 16px rgba(0,0,0,0.25)',
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent',
    userSelect: 'none'
  };

  return (
    <div style={backdropStyle} onClick={kb.close}>
      <div style={panelStyle} onClick={(e) => e.stopPropagation()}>
        {kb.type === 'numeric' ? (
          <NumericKeypad />
        ) : (
          <AlphaKeyboard />
        )}
      </div>
    </div>
  );
};

// Lazy import to avoid circular issues within a single file
const NumericKeypad = React.lazy(() => import('../components/common/keyboard/NumericKeypad'));
const AlphaKeyboard = React.lazy(() => import('../components/common/keyboard/AlphaKeyboard'));
